import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { formSchema, type FormData } from '../lib/schema';
import { generateProposal, storeProposal } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import LoadingOverlay from './LoadingOverlay';

export default function ProposalForm() {
  const [isLoading, setIsLoading] = React.useState(false);
  const navigate = useNavigate();
  
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      company: '',
      businessDescription: '',
      problem: '',
      solution: '',
      platforms: '',
      projectDuration: '',
      budget: '',
      additionalCosts: '',
      callOutcome: '',
    },
  });

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    try {
      const proposalText = await generateProposal(data);
      await storeProposal(data, proposalText);
      navigate('/result', { 
        state: { 
          proposal: proposalText,
          formData: data
        } 
      });
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      {isLoading && <LoadingOverlay />}
      
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="firstName">First Name</Label>
            <Input {...form.register('firstName')} placeholder="John" />
            {form.formState.errors.firstName && (
              <p className="text-sm text-red-500">{form.formState.errors.firstName.message}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="lastName">Last Name</Label>
            <Input {...form.register('lastName')} placeholder="Doe" />
            {form.formState.errors.lastName && (
              <p className="text-sm text-red-500">{form.formState.errors.lastName.message}</p>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input {...form.register('email')} type="email" placeholder="john@example.com" />
          {form.formState.errors.email && (
            <p className="text-sm text-red-500">{form.formState.errors.email.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="company">Company</Label>
          <Input {...form.register('company')} placeholder="Company Name" />
          {form.formState.errors.company && (
            <p className="text-sm text-red-500">{form.formState.errors.company.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="businessDescription">Business Description</Label>
          <Textarea 
            {...form.register('businessDescription')} 
            placeholder="Describe your business..."
            className="min-h-[100px]"
          />
          {form.formState.errors.businessDescription && (
            <p className="text-sm text-red-500">{form.formState.errors.businessDescription.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="problem">Problem Statement</Label>
          <Textarea 
            {...form.register('problem')} 
            placeholder="What problem are you trying to solve?"
            className="min-h-[100px]"
          />
          {form.formState.errors.problem && (
            <p className="text-sm text-red-500">{form.formState.errors.problem.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="solution">Proposed Solution</Label>
          <Textarea 
            {...form.register('solution')} 
            placeholder="How do you plan to solve this problem?"
            className="min-h-[100px]"
          />
          {form.formState.errors.solution && (
            <p className="text-sm text-red-500">{form.formState.errors.solution.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="platforms">Platforms/Services Required</Label>
          <Input 
            {...form.register('platforms')} 
            placeholder="e.g. Web Application, Mobile App, API Integration"
          />
          {form.formState.errors.platforms && (
            <p className="text-sm text-red-500">{form.formState.errors.platforms.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="projectDuration">Project Duration</Label>
          <Input 
            {...form.register('projectDuration')} 
            placeholder="e.g. 3 months"
          />
          {form.formState.errors.projectDuration && (
            <p className="text-sm text-red-500">{form.formState.errors.projectDuration.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="budget">Project Cost</Label>
          <Input 
            {...form.register('budget')} 
            placeholder="$500"
          />
          {form.formState.errors.budget && (
            <p className="text-sm text-red-500">{form.formState.errors.budget.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="additionalCosts">Additional Costs (Optional)</Label>
          <Textarea 
            {...form.register('additionalCosts')} 
            placeholder="Any additional costs or considerations..."
            className="min-h-[100px]"
          />
          {form.formState.errors.additionalCosts && (
            <p className="text-sm text-red-500">{form.formState.errors.additionalCosts.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="callOutcome">Call Outcome</Label>
          <Textarea 
            {...form.register('callOutcome')} 
            placeholder="Summary of the discussion and next steps..."
            className="min-h-[100px]"
          />
          {form.formState.errors.callOutcome && (
            <p className="text-sm text-red-500">{form.formState.errors.callOutcome.message}</p>
          )}
        </div>

        <Button type="submit" className="w-full">
          Generate Proposal
        </Button>
      </form>
    </div>
  );
}